import { useState, useCallback } from "react";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
	AlertDialogAction,
} from "@/dentlab/src/components/ui/alert-dialog";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import { supabase } from "@/lib/supabase";
import { StorageBucketsEnum } from "@/dentlab/src/types/enums";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { Logger } from "@/lib/logger/Logger";
import { PreCheckDeleteJobResult } from "@/dentlab/src/types/delete-job.types";
import { createSecurePatientName } from "@/dentlab/src/lib/utils/utils-functions";

const DOC_TYPE_LABELS: Record<string, string> = {
	dn: "Lieferschein",
	qt: "Kostenvoranschlag",
	cn: "Gutschrift",
	materials: "Rückverfolgung",
};

const DOC_STATUS_LABELS: Record<number, string> = {
	1: "Pendent",
	2: "Geliefert",
	3: "Archiviert",
};

interface DeleteJobConfirmDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	jobId: number;
	preCheckData: PreCheckDeleteJobResult;
	onDeleted?: () => void;
}

export const DeleteJobConfirmDialog: React.FC<DeleteJobConfirmDialogProps> = ({
	open,
	onOpenChange,
	jobId,
	preCheckData,
	onDeleted,
}) => {
	const [deletePatient, setDeletePatient] = useState(true);
	const [loading, setLoading] = useState(false);

	const hardDeleteJob = useJobStore((state) => state.hardDeleteJob);
	const isPrivacyOn = useCentralStore(
		(state) => !!state.profile?.data_privacy_mode
	);

	const patientName = preCheckData.patient
		? createSecurePatientName(
				preCheckData.patient.id,
				preCheckData.patient.first_name ?? "",
				preCheckData.patient.last_name ?? "",
				isPrivacyOn
			)
		: "";

	const handleConfirm = useCallback(
		async (e: React.MouseEvent) => {
			e.preventDefault();
			setLoading(true);

			const filePaths = (preCheckData.files ?? [])
				.map((f) => f.path_name)
				.filter(Boolean);

			if (filePaths.length > 0) {
				const { error } = await supabase.storage
					.from(StorageBucketsEnum.V1)
					.remove(filePaths);
				if (error) {
					Logger.error(
						error,
						{},
						"Error deleting files from storage"
					);
					showNotification({
						message: "Fehler beim Löschen der Dateien",
						type: "error",
					});
					setLoading(false);
					return;
				}
			}

			const shouldDeletePatient =
				deletePatient && preCheckData.patient?.is_only_job === true;

			const success = await hardDeleteJob(jobId, shouldDeletePatient);
			setLoading(false);

			if (success) {
				onOpenChange(false);
				onDeleted?.();
			}
		},
		[
			preCheckData,
			deletePatient,
			hardDeleteJob,
			jobId,
			onOpenChange,
			onDeleted,
		]
	);

	const documents = preCheckData.documents ?? [];
	const fileCount = preCheckData.file_count ?? 0;
	const showPatientCheckbox = preCheckData.patient?.is_only_job === true;

	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent data-testid="delete-job-dialog">
				<AlertDialogHeader>
					<AlertDialogTitle>
						Auftrag endgültig löschen?
					</AlertDialogTitle>
					<AlertDialogDescription asChild>
						<div className="space-y-3">
							<p>
								Folgende Daten werden unwiderruflich gelöscht:
							</p>

							{documents.length > 0 && (
								<div>
									<p className="font-medium text-foreground text-sm">
										Dokumente:
									</p>
									<ul className="list-disc pl-5 mt-1 space-y-0.5">
										{documents.map((doc) => (
											<li
												key={doc.id}
												className="text-sm"
											>
												{DOC_TYPE_LABELS[doc.type] ??
													doc.type}{" "}
												(
												{DOC_STATUS_LABELS[
													doc.status
												] ?? `Status ${doc.status}`}
												)
												{doc.amount != null &&
													doc.amount > 0 &&
													` — CHF ${doc.amount.toFixed(
														2
													)}`}
											</li>
										))}
									</ul>
								</div>
							)}

							{fileCount > 0 && (
								<p className="text-sm">
									<span className="font-medium text-foreground">
										Dateien:
									</span>{" "}
									{fileCount}{" "}
									{fileCount === 1 ? "Datei" : "Dateien"}
								</p>
							)}

							{showPatientCheckbox && (
								<div className="flex items-start gap-2 pt-1">
									<Checkbox
										id="delete-patient"
										data-testid="delete-job-dialog-patient-checkbox"
										checked={deletePatient}
										onCheckedChange={(checked) =>
											setDeletePatient(checked as boolean)
										}
									/>
									<div className="grid gap-0.5 leading-none">
										<label
											htmlFor="delete-patient"
											className="text-sm font-medium text-foreground cursor-pointer"
										>
											Patient {patientName} ebenfalls
											löschen
										</label>
										<p className="text-xs text-muted-foreground">
											(wird in keinem anderen Auftrag
											verwendet)
										</p>
									</div>
								</div>
							)}
						</div>
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel
						data-testid="delete-job-dialog-cancel"
						disabled={loading}
					>
						Abbrechen
					</AlertDialogCancel>
					<AlertDialogAction
						data-testid="delete-job-dialog-confirm"
						onClick={handleConfirm}
						disabled={loading}
						className="bg-destructive text-white hover:bg-destructive/90"
					>
						{loading ? "Wird gelöscht..." : "Endgültig löschen"}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};
